import React, { useEffect, useRef, useState } from 'react';

type Props = {
  label: string;
  signerName: string;
  savedImage?: string | null;
  canSign: boolean;
  busy?: boolean;
  onSave: (dataUrl: string) => Promise<void> | void;
};

const CANVAS_WIDTH = 560;
const CANVAS_HEIGHT = 180;

function eventPoint(event: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) {
  const rect = canvas.getBoundingClientRect();
  const source = 'touches' in event ? event.touches[0] : event;
  if (!source) return null;
  return {
    x: ((source.clientX - rect.left) / rect.width) * canvas.width,
    y: ((source.clientY - rect.top) / rect.height) * canvas.height,
  };
}

const HandwrittenSignaturePad: React.FC<Props> = ({
  label,
  signerName,
  savedImage,
  canSign,
  busy = false,
  onSave,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const hasInkRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d', { willReadFrequently: true });
    if (!canvas || !ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    hasInkRef.current = false;
    setHasInk(false);
  };

  useEffect(() => {
    if (!canSign || savedImage) return;
    clearCanvas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSign, savedImage]);

  const startDraw = (event: React.MouseEvent | React.TouchEvent) => {
    if (!canSign || busy) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d', { willReadFrequently: true });
    const point = canvas ? eventPoint(event, canvas) : null;
    if (!canvas || !ctx || !point) return;
    drawingRef.current = true;
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 2.6;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const moveDraw = (event: React.MouseEvent | React.TouchEvent) => {
    if (!drawingRef.current || !canSign) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d', { willReadFrequently: true });
    const point = canvas ? eventPoint(event, canvas) : null;
    if (!ctx || !point) return;
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    if (!hasInkRef.current) {
      hasInkRef.current = true;
      setHasInk(true);
    }
  };

  const endDraw = () => {
    drawingRef.current = false;
  };

  const handleSave = async () => {
    if (!canSign || busy) return;
    const canvas = canvasRef.current;
    if (!canvas || !hasInkRef.current) return;
    await onSave(canvas.toDataURL('image/png'));
  };

  return (
    <div className="border-t border-[#e7dcc3] pt-4">
      <p className="mb-1 font-bold uppercase text-[#1f2937]">{label}</p>
      <p className="mb-3 text-sm text-gray-600">
        {savedImage ? 'Đã ký' : canSign ? 'Vẽ chữ ký vào ô bên dưới' : 'Chờ bên này ký'}
      </p>
      {savedImage ? (
        <div className="rounded-xl border border-[#dcc79d] bg-white px-3 py-2">
          <img src={savedImage} alt={`Chữ ký ${label}`} className="mx-auto h-28 w-full object-contain" />
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border-2 border-dashed border-[#C4A35A] bg-white">
            <canvas
              ref={canvasRef}
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              className={`h-32 w-full touch-none ${canSign ? 'cursor-crosshair' : 'cursor-not-allowed bg-gray-50'}`}
              onMouseDown={canSign ? startDraw : undefined}
              onMouseMove={canSign ? moveDraw : undefined}
              onMouseUp={canSign ? endDraw : undefined}
              onMouseLeave={canSign ? endDraw : undefined}
              onTouchStart={canSign ? startDraw : undefined}
              onTouchMove={canSign ? moveDraw : undefined}
              onTouchEnd={canSign ? endDraw : undefined}
            />
          </div>
          {canSign && (
            <div className="mt-3 flex gap-2 print:hidden">
              <button
                type="button"
                onClick={clearCanvas}
                disabled={busy || !hasInk}
                className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 disabled:opacity-50"
              >
                Xóa
              </button>
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={busy || !hasInk}
                className="flex-1 rounded-lg bg-[#C4A35A] px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy ? 'Đang lưu...' : 'Xác nhận chữ ký'}
              </button>
            </div>
          )}
        </>
      )}
      <p className="mt-3 font-semibold text-gray-800">{signerName}</p>
    </div>
  );
};

export default HandwrittenSignaturePad;
